/**
 * Skill registry / assignment / execution.
 * Permanent Vanguard actives are gated by unlockedActiveSkillIds for offers.
 */

import {
  SKILL_BY_ID,
  VANGUARD_PROVISIONAL_PRIMARY_SKILLS,
} from '../data/skills';
import { VANGUARD_PERMANENT_ACTIVES } from '../data/vanguardActives';
import type { CharacterCombatStats } from './characterStats';
import {
  applyOutgoingDamageBonuses,
  rollCritDamage,
} from './characterStats';
import {
  consecutiveCritBonusPp,
  recordAttackCritOutcome,
  type AttackSequenceCritState,
  type ChainedAttackHit,
  type ConsecutiveCritStackingMode,
} from './attackSequence';
import { OFFHAND_DAMAGE_CONTRIBUTION } from './equipmentCombat';
import type { SeededRng } from './rng';
import type {
  DieState,
  DiceTrayState,
  EnemyState,
  PlayerCombatState,
  SkillDefinition,
  SkillOffer,
  StatusInstance,
  VanguardActiveEffect,
} from './types';
import { MAX_SKILL_LEVEL } from './types';

export function getSkillDefinition(skillId: string): SkillDefinition {
  const skill = SKILL_BY_ID[skillId];
  if (!skill) {
    throw new Error(`Unknown skill: ${skillId}`);
  }
  return skill;
}

/**
 * Offer pool for dungeon skill choices.
 * - If permanent actives are unlocked → only those (locked permanents never appear).
 * - If none unlocked → provisional M3 pool so a fresh run remains playable.
 */
export function listPrimarySkillPool(
  unlockedActiveSkillIds: readonly string[] = [],
): readonly SkillDefinition[] {
  if (unlockedActiveSkillIds.length === 0) {
    return VANGUARD_PROVISIONAL_PRIMARY_SKILLS;
  }
  const unlocked = new Set(unlockedActiveSkillIds);
  return VANGUARD_PERMANENT_ACTIVES.filter((skill) => unlocked.has(skill.id));
}

export function skillAmountAtLevel(
  base: number,
  perLevel: number,
  level: number,
): number {
  return base + perLevel * Math.max(0, level - 1);
}

function levelTableValue(
  table: readonly number[] | undefined,
  level: number,
): number {
  if (!table || table.length === 0) return 0;
  const idx = Math.max(0, Math.min(table.length - 1, level - 1));
  return table[idx]!;
}

export function findDieWithSkill(
  tray: DiceTrayState,
  skillId: string,
): DieState | undefined {
  return tray.dice.find(
    (die) => die.unlocked && die.primarySkillId === skillId,
  );
}

export function emptyUnlockedDice(tray: DiceTrayState): DieState[] {
  return tray.dice.filter(
    (die) => die.unlocked && die.primarySkillId === null,
  );
}

export function buildSkillOffer(
  skill: SkillDefinition,
  tray: DiceTrayState,
): SkillOffer {
  const owned = findDieWithSkill(tray, skill.id);
  const currentLevel = owned?.primarySkillLevel ?? 0;
  const isUpgrade = currentLevel > 0 && currentLevel < skill.maxLevel;
  return {
    offerId: skill.id,
    kind: 'skill',
    skillId: skill.id,
    isUpgrade,
    currentLevel,
    nextLevel: isUpgrade ? currentLevel + 1 : 1,
  };
}

export function assignSkillToDie(
  tray: DiceTrayState,
  dieId: number,
  skillId: string,
): DiceTrayState {
  getSkillDefinition(skillId);
  const target = tray.dice.find((die) => die.id === dieId);
  if (!target) {
    throw new Error(`Unknown die id: ${dieId}`);
  }
  if (!target.unlocked) {
    throw new Error(`Cannot assign skill to locked die ${dieId}`);
  }
  if (target.primarySkillId !== null) {
    throw new Error(`Die ${dieId} already has a skill; use replaceSkillOnDie`);
  }
  return {
    unlockedCount: tray.unlockedCount,
    dice: tray.dice.map((die) =>
      die.id === dieId
        ? { ...die, primarySkillId: skillId, primarySkillLevel: 1 }
        : { ...die },
    ),
  };
}

export function replaceSkillOnDie(
  tray: DiceTrayState,
  dieId: number,
  skillId: string,
): DiceTrayState {
  getSkillDefinition(skillId);
  const target = tray.dice.find((die) => die.id === dieId);
  if (!target) {
    throw new Error(`Unknown die id: ${dieId}`);
  }
  if (!target.unlocked) {
    throw new Error(`Cannot assign skill to locked die ${dieId}`);
  }
  return {
    unlockedCount: tray.unlockedCount,
    dice: tray.dice.map((die) =>
      die.id === dieId
        ? { ...die, primarySkillId: skillId, primarySkillLevel: 1 }
        : { ...die },
    ),
  };
}

export function upgradeSkillOnDie(
  tray: DiceTrayState,
  skillId: string,
): DiceTrayState {
  const skill = getSkillDefinition(skillId);
  const owned = findDieWithSkill(tray, skillId);
  if (!owned) {
    throw new Error(`Skill ${skillId} is not assigned to any die`);
  }
  if (owned.primarySkillLevel >= skill.maxLevel) {
    throw new Error(`Skill ${skillId} is already max level`);
  }
  return {
    unlockedCount: tray.unlockedCount,
    dice: tray.dice.map((die) =>
      die.id === owned.id
        ? {
            ...die,
            primarySkillLevel: Math.min(
              MAX_SKILL_LEVEL,
              die.primarySkillLevel + 1,
            ),
          }
        : { ...die },
    ),
  };
}

export function clearAllDiceSkills(tray: DiceTrayState): DiceTrayState {
  return {
    unlockedCount: tray.unlockedCount,
    dice: tray.dice.map((die) => ({
      ...die,
      primarySkillId: null,
      primarySkillLevel: 0,
    })),
  };
}

export interface SkillExecutionResult {
  summary: string;
  damage: number;
  shieldGained: number;
  statusApplied?: StatusIdLike;
  player: PlayerCombatState;
  enemies: EnemyState[];
  targetId?: string;
  /** Total damage dealt across all enemies this activation (for Iron Harvest). */
  totalDamageDealt?: number;
  /** Updated attack-sequence crit streak after chained hits (Flurry / Twin Strike). */
  attackSequence?: AttackSequenceCritState;
  /** Per-hit details for combat events / logs. */
  chainedHits?: ChainedAttackHit[];
}

type StatusIdLike = StatusInstance['id'];

function upsertStatus(
  statuses: StatusInstance[],
  next: StatusInstance,
): StatusInstance[] {
  if (next.id === 'bleed' || next.id === 'vulnerable') {
    // Stack: add intensity; refresh duration for bleed/vulnerable.
    const existing = statuses.find((s) => s.id === next.id);
    if (existing) {
      return statuses.map((s) =>
        s.id === next.id
          ? {
              ...s,
              intensity: s.intensity + next.intensity,
              remainingTurns: Math.max(s.remainingTurns, next.remainingTurns),
            }
          : s,
      );
    }
  }
  const without = statuses.filter((s) => s.id !== next.id);
  return [...without, next];
}

export interface ExecuteSkillOptions {
  combatStats?: CharacterCombatStats;
  /** Base weapon/combo damage used by next-attack % bonuses and Flurry/Twin Strike. */
  activatingAttackDamage?: number;
  /** Damage already dealt this resolution (Cleave/Harvest). */
  damageAlreadyDealt?: number;
  rng?: SeededRng;
  /**
   * Crit streak for this player attack sequence. Mutated conceptually via
   * returned SkillExecutionResult.attackSequence.
   */
  attackSequence?: AttackSequenceCritState;
  /** Test override for consecutive-crit stacking mode (production = accumulate). */
  consecutiveCritStackingMode?: ConsecutiveCritStackingMode;
  /**
   * Extra crit chance pp for Twin Strike only (Precise Twin Strike enhancement).
   */
  twinStrikeExtraCritChancePp?: number;
  /** Extra % on Twin Strike off-hand hit (Deadly Twin Strike). */
  twinStrikeDamageBonusPct?: number;
}

/** Next-attack actives modify the primary hit; post-attack actives append after. */
const NEXT_ATTACK_ACTIVE_IDS = new Set([
  'cleave',
  'rend',
  'bloodlust',
  'shield_breaker',
  'sunder',
]);

export interface ParticipatingSkillRef {
  dieId: number;
  skill: SkillDefinition;
  level: number;
}

export interface NextAttackSkillPlan {
  /** Additive % bonuses applied to the primary activating attack. */
  primaryDamageBonusPct: number;
  stripShieldBeforeDamage: boolean;
  guaranteedBleedStacks: number;
  /** Highest Cleave splash % among participating Cleaves (spec: one cleave typical). */
  cleaveSplashPctOfPrimary: number | null;
  bloodlustHealMaxHpPct: number | null;
  applyVulnerableStacks: number;
  /** UI/event rows for next-attack modifiers (no separate bonus hit). */
  nextAttackActivations: Array<{
    dieId: number;
    skillId: string;
    skillName: string;
    skillLevel: number;
    summary: string;
    statusApplied?: StatusIdLike;
  }>;
  /** Skills that still run via executePrimarySkill after the primary hit. */
  postAttackSkills: ParticipatingSkillRef[];
}

/**
 * Classifies participating dice skills into next-attack modifiers vs post-attack
 * append skills. Next-attack % bonuses stack additively on the primary hit.
 */
export function planParticipatingSkills(
  tray: DiceTrayState,
  participatingDieIds: readonly number[],
  /**
   * Selected enhancement ids for this run. Bonuses apply only when the
   * parent skill participates in the attack (spec: enhance that skill).
   */
  selectedEnhancementIds: readonly string[] = [],
): NextAttackSkillPlan {
  const ordered = [...participatingDieIds].sort((a, b) => a - b);
  let primaryDamageBonusPct = 0;
  let stripShieldBeforeDamage = false;
  let guaranteedBleedStacks = 0;
  let cleaveSplashPctOfPrimary: number | null = null;
  let bloodlustHealMaxHpPct: number | null = null;
  let applyVulnerableStacks = 0;
  const nextAttackActivations: NextAttackSkillPlan['nextAttackActivations'] = [];
  const postAttackSkills: ParticipatingSkillRef[] = [];
  const hasEnh = (id: string) => selectedEnhancementIds.includes(id);

  for (const dieId of ordered) {
    const die = tray.dice.find((d) => d.id === dieId);
    if (!die?.unlocked || !die.primarySkillId || die.primarySkillLevel <= 0) {
      continue;
    }
    const skill = getSkillDefinition(die.primarySkillId);
    const level = die.primarySkillLevel;

    if (skill.effect.type !== 'vanguard_active') {
      postAttackSkills.push({ dieId, skill, level });
      continue;
    }

    const effect = skill.effect;
    if (!NEXT_ATTACK_ACTIVE_IDS.has(effect.activeId)) {
      postAttackSkills.push({ dieId, skill, level });
      continue;
    }

    const parts: string[] = [];
    let statusApplied: StatusIdLike | undefined;

    switch (effect.activeId) {
      case 'cleave': {
        let pct = levelTableValue(effect.primaryDamagePctByLevel, level);
        if (hasEnh('heavy_cleave')) pct += 10;
        let splash = levelTableValue(effect.splashPctOfPrimaryByLevel, level);
        if (hasEnh('wide_cleave')) splash += 10;
        primaryDamageBonusPct += pct;
        cleaveSplashPctOfPrimary = Math.max(
          cleaveSplashPctOfPrimary ?? 0,
          splash,
        );
        parts.push(`attack +${pct}%`, `splash ${splash}%`);
        break;
      }
      case 'rend': {
        let pct = levelTableValue(effect.primaryDamagePctByLevel, level);
        if (hasEnh('savage_rend')) pct += 10;
        let stacks = levelTableValue(
          effect.guaranteedBleedStacksByLevel,
          level,
        );
        if (hasEnh('deep_wounds')) stacks += 1;
        primaryDamageBonusPct += pct;
        guaranteedBleedStacks += stacks;
        statusApplied = 'bleed';
        parts.push(`attack +${pct}%`, `bleed +${stacks}`);
        break;
      }
      case 'bloodlust': {
        let healPct = levelTableValue(effect.healMaxHpPctByLevel, level);
        if (hasEnh('greater_bloodlust')) healPct += 1;
        if (hasEnh('vampiric_strike')) primaryDamageBonusPct += 10;
        bloodlustHealMaxHpPct = (bloodlustHealMaxHpPct ?? 0) + healPct;
        parts.push(`heal ${healPct}% max HP on hit`);
        break;
      }
      case 'shield_breaker': {
        const pct = levelTableValue(effect.primaryDamagePctByLevel, level);
        stripShieldBeforeDamage = true;
        primaryDamageBonusPct += pct;
        parts.push('strip shield', `attack +${pct}%`);
        break;
      }
      case 'sunder': {
        const pct = levelTableValue(effect.primaryDamagePctByLevel, level);
        primaryDamageBonusPct += pct;
        applyVulnerableStacks += effect.applyVulnerableStacks ?? 1;
        statusApplied = 'vulnerable';
        parts.push(`attack +${pct}%`, 'vulnerable +1 after');
        break;
      }
      default:
        break;
    }

    nextAttackActivations.push({
      dieId,
      skillId: skill.id,
      skillName: skill.name,
      skillLevel: level,
      summary: `${skill.name} Lv${level}: ${parts.join(', ')}`,
      statusApplied,
    });
  }

  return {
    primaryDamageBonusPct,
    stripShieldBeforeDamage,
    guaranteedBleedStacks,
    cleaveSplashPctOfPrimary,
    bloodlustHealMaxHpPct,
    applyVulnerableStacks,
    nextAttackActivations,
    postAttackSkills,
  };
}

export function applyStatusToEnemy(
  enemies: EnemyState[],
  enemyId: string,
  statusId: StatusIdLike,
  intensity: number,
  durationTurns: number,
): EnemyState[] {
  return enemies.map((e) => {
    if (e.id !== enemyId || e.hp <= 0) return e;
    return {
      ...e,
      statuses: upsertStatus(e.statuses, {
        id: statusId,
        intensity,
        remainingTurns: durationTurns,
      }),
    };
  });
}

/**
 * Splashes Cleave damage to every living non-primary enemy.
 * Splash equals splashPct of actual final primary-target damage dealt.
 */
export function applyCleaveSplash(
  enemies: EnemyState[],
  primaryTargetId: string,
  primaryDamageDealt: number,
  splashPct: number,
): { enemies: EnemyState[]; totalSplash: number; summaries: string[] } {
  if (primaryDamageDealt <= 0 || splashPct <= 0) {
    return { enemies, totalSplash: 0, summaries: [] };
  }
  const splash = Math.floor((primaryDamageDealt * splashPct) / 100);
  if (splash <= 0) {
    return { enemies, totalSplash: 0, summaries: [] };
  }
  let next = enemies;
  let totalSplash = 0;
  const summaries: string[] = [];
  for (const enemy of enemies) {
    if (enemy.id === primaryTargetId || enemy.hp <= 0) continue;
    let remaining = splash;
    let sh = enemy.shield ?? 0;
    if (sh > 0) {
      const ab = Math.min(sh, remaining);
      sh -= ab;
      remaining -= ab;
    }
    const mit = Math.max(0, remaining - enemy.intention.guardAmount);
    const hpAfter = Math.max(0, enemy.hp - mit);
    next = next.map((e) =>
      e.id === enemy.id ? { ...e, hp: hpAfter, shield: sh } : e,
    );
    totalSplash += mit;
    if (mit > 0) {
      summaries.push(`${enemy.name} splash ${mit}`);
    }
  }
  return { enemies: next, totalSplash, summaries };
}

/**
 * Executes a primary dice skill against the current target (or self for shields).
 * Damage is mitigated by the target's current guard intention.
 */
export function executePrimarySkill(
  skill: SkillDefinition,
  level: number,
  player: PlayerCombatState,
  enemies: EnemyState[],
  targetId: string | null,
  options: ExecuteSkillOptions = {},
): SkillExecutionResult {
  let damage = 0;
  let shieldGained = 0;
  let statusApplied: StatusIdLike | undefined;
  let totalDamageDealt = 0;
  let nextPlayer = { ...player, statuses: [...player.statuses] };
  let nextEnemies = enemies.map((e) => ({
    ...e,
    shield: e.shield ?? 0,
    statuses: [...e.statuses],
  }));
  const parts: string[] = [];
  const stats = options.combatStats;
  const baseAttack = options.activatingAttackDamage ?? 0;

  const applyDamageTo = (id: string, amount: number): number => {
    if (amount <= 0) return 0;
    const target = nextEnemies.find((e) => e.id === id);
    if (!target || target.hp <= 0) return 0;
    let raw = amount;
    if (stats) {
      raw = applyOutgoingDamageBonuses(raw, stats, {
        targetHp: target.hp,
        targetMaxHp: target.maxHp,
        targetIsBleeding: target.statuses.some((s) => s.id === 'bleed'),
      });
    }
    // Enemy accumulated shield absorbs before HP (Shield Breaker strips first).
    let remaining = raw;
    let enemyShield = target.shield ?? 0;
    if (enemyShield > 0) {
      const absorbed = Math.min(enemyShield, remaining);
      enemyShield -= absorbed;
      remaining -= absorbed;
    }
    const mitigated = Math.max(0, remaining - target.intention.guardAmount);
    const hpAfter = Math.max(0, target.hp - mitigated);
    nextEnemies = nextEnemies.map((e) =>
      e.id === id ? { ...e, hp: hpAfter, shield: enemyShield } : e,
    );
    totalDamageDealt += mitigated;
    return mitigated;
  };

  const applyDamage = (amount: number): number => {
    if (!targetId) return 0;
    return applyDamageTo(targetId, amount);
  };

  const applyStatus = (
    statusId: StatusIdLike,
    intensity: number,
    durationTurns: number,
    id: string | null = targetId,
  ): void => {
    if (!id) return;
    const target = nextEnemies.find((e) => e.id === id);
    if (!target || target.hp <= 0) return;
    statusApplied = statusId;
    nextEnemies = nextEnemies.map((e) =>
      e.id === id
        ? {
            ...e,
            statuses: upsertStatus(e.statuses, {
              id: statusId,
              intensity,
              remainingTurns: durationTurns,
            }),
          }
        : e,
    );
  };

  if (skill.effect.type === 'vanguard_active') {
    return executeVanguardActive(
      skill,
      skill.effect,
      level,
      nextPlayer,
      nextEnemies,
      targetId,
      parts,
      applyDamage,
      applyDamageTo,
      applyStatus,
      baseAttack,
      options,
    );
  }

  switch (skill.effect.type) {
    case 'damage': {
      const raw = skillAmountAtLevel(
        skill.effect.baseAmount,
        skill.effect.perLevel,
        level,
      );
      damage = applyDamage(raw);
      parts.push(`${damage} dmg`);
      break;
    }
    case 'shield': {
      shieldGained = skillAmountAtLevel(
        skill.effect.baseAmount,
        skill.effect.perLevel,
        level,
      );
      nextPlayer = {
        ...nextPlayer,
        shield: nextPlayer.shield + shieldGained,
      };
      parts.push(`+${shieldGained} shield`);
      break;
    }
    case 'status': {
      const intensity = skillAmountAtLevel(
        skill.effect.baseIntensity,
        skill.effect.perLevel,
        level,
      );
      applyStatus(
        skill.effect.statusId,
        intensity,
        skill.effect.durationTurns,
      );
      parts.push(`${skill.effect.statusId} ${intensity}`);
      break;
    }
    case 'damage_and_status': {
      const raw = skillAmountAtLevel(
        skill.effect.baseDamage,
        skill.effect.damagePerLevel,
        level,
      );
      damage = applyDamage(raw);
      const intensity = skillAmountAtLevel(
        skill.effect.baseIntensity,
        skill.effect.intensityPerLevel,
        level,
      );
      applyStatus(
        skill.effect.statusId,
        intensity,
        skill.effect.durationTurns,
      );
      parts.push(`${damage} dmg`, `${skill.effect.statusId}`);
      break;
    }
    case 'damage_and_shield': {
      const raw = skillAmountAtLevel(
        skill.effect.baseDamage,
        skill.effect.damagePerLevel,
        level,
      );
      damage = applyDamage(raw);
      shieldGained = skillAmountAtLevel(
        skill.effect.baseShield,
        skill.effect.shieldPerLevel,
        level,
      );
      nextPlayer = {
        ...nextPlayer,
        shield: nextPlayer.shield + shieldGained,
      };
      parts.push(`${damage} dmg`, `+${shieldGained} shield`);
      break;
    }
    default: {
      const _exhaustive: never = skill.effect;
      return _exhaustive;
    }
  }

  return {
    summary: `${skill.name} Lv${level}: ${parts.join(', ')}`,
    damage,
    shieldGained,
    statusApplied,
    player: nextPlayer,
    enemies: nextEnemies,
    targetId: targetId ?? undefined,
    totalDamageDealt,
  };
}

function executeVanguardActive(
  skill: SkillDefinition,
  effect: VanguardActiveEffect,
  level: number,
  player: PlayerCombatState,
  enemies: EnemyState[],
  targetId: string | null,
  parts: string[],
  applyDamage: (n: number) => number,
  applyDamageTo: (id: string, n: number) => number,
  applyStatus: (
    statusId: StatusIdLike,
    intensity: number,
    durationTurns: number,
    id?: string | null,
  ) => void,
  baseAttack: number,
  options: ExecuteSkillOptions,
): SkillExecutionResult {
  let nextPlayer = { ...player, statuses: [...player.statuses] };
  let nextEnemies = enemies;
  let damage = 0;
  let shieldGained = 0;
  let statusApplied: StatusIdLike | undefined;
  let totalDamageDealt = 0;

  // Rebind helpers to mutate local copies via wrappers
  const dmg = (amount: number): number => {
    // applyDamage closes over nextEnemies from outer executePrimarySkill —
    // we need to work on the enemies passed in. Simpler: inline.
    if (!targetId || amount <= 0) return 0;
    const target = nextEnemies.find((e) => e.id === targetId);
    if (!target || target.hp <= 0) return 0;
    let raw = amount;
    const stats = options.combatStats;
    if (stats) {
      raw = applyOutgoingDamageBonuses(raw, stats, {
        targetHp: target.hp,
        targetMaxHp: target.maxHp,
        targetIsBleeding: target.statuses.some((s) => s.id === 'bleed'),
      });
    }
    let remaining = raw;
    let enemyShield = target.shield ?? 0;
    if (enemyShield > 0) {
      const absorbed = Math.min(enemyShield, remaining);
      enemyShield -= absorbed;
      remaining -= absorbed;
    }
    const mitigated = Math.max(0, remaining - target.intention.guardAmount);
    const hpAfter = Math.max(0, target.hp - mitigated);
    nextEnemies = nextEnemies.map((e) =>
      e.id === targetId ? { ...e, hp: hpAfter, shield: enemyShield } : e,
    );
    totalDamageDealt += mitigated;
    return mitigated;
  };

  const setStatus = (
    statusId: StatusIdLike,
    intensity: number,
    durationTurns: number,
    id: string | null = targetId,
  ): void => {
    if (!id) return;
    const target = nextEnemies.find((e) => e.id === id);
    if (!target || target.hp <= 0) return;
    statusApplied = statusId;
    const existing = target.statuses.find((s) => s.id === statusId);
    const nextStatuses =
      statusId === 'bleed' || statusId === 'vulnerable'
        ? existing
          ? target.statuses.map((s) =>
              s.id === statusId
                ? {
                    ...s,
                    intensity: s.intensity + intensity,
                    remainingTurns: Math.max(s.remainingTurns, durationTurns),
                  }
                : s,
            )
          : [
              ...target.statuses,
              { id: statusId, intensity, remainingTurns: durationTurns },
            ]
        : [
            ...target.statuses.filter((s) => s.id !== statusId),
            { id: statusId, intensity, remainingTurns: durationTurns },
          ];
    nextEnemies = nextEnemies.map((e) =>
      e.id === id ? { ...e, statuses: nextStatuses } : e,
    );
  };

  switch (effect.activeId) {
    case 'cleave': {
      // Standalone helper: one modified primary hit + splash.
      // Combat resolution applies Cleave via planParticipatingSkills instead.
      const pct = levelTableValue(effect.primaryDamagePctByLevel, level);
      const splashPct = levelTableValue(effect.splashPctOfPrimaryByLevel, level);
      const primary = Math.floor((baseAttack * (100 + pct)) / 100);
      damage = dmg(primary);
      parts.push(`attack +${pct}% (${damage} dmg)`);
      if (damage > 0 && targetId) {
        const splashResult = applyCleaveSplash(
          nextEnemies,
          targetId,
          damage,
          splashPct,
        );
        nextEnemies = splashResult.enemies;
        totalDamageDealt += splashResult.totalSplash;
        parts.push(...splashResult.summaries);
      }
      break;
    }
    case 'rend': {
      const pct = levelTableValue(effect.primaryDamagePctByLevel, level);
      const stacks = levelTableValue(effect.guaranteedBleedStacksByLevel, level);
      const primary = Math.floor((baseAttack * (100 + pct)) / 100);
      damage = dmg(primary);
      if (targetId) {
        setStatus('bleed', stacks, 99);
      }
      parts.push(`attack +${pct}% (${damage} dmg)`, `bleed +${stacks}`);
      break;
    }
    case 'bloodlust': {
      const healPct = levelTableValue(effect.healMaxHpPctByLevel, level);
      // Heal triggers on successful activating attack — caller passes damageAlreadyDealt.
      const activatingDealt = options.damageAlreadyDealt ?? 0;
      if (activatingDealt > 0) {
        const heal = Math.max(
          1,
          Math.floor(nextPlayer.maxHp * (healPct / 100)),
        );
        nextPlayer = {
          ...nextPlayer,
          hp: Math.min(nextPlayer.maxHp, nextPlayer.hp + heal),
        };
        parts.push(`heal ${heal} (${healPct}% max HP)`);
      } else {
        parts.push('no heal (attack dealt 0)');
      }
      break;
    }
    case 'shield_breaker': {
      if (targetId) {
        nextEnemies = nextEnemies.map((e) =>
          e.id === targetId ? { ...e, shield: 0 } : e,
        );
        parts.push('shield stripped');
      }
      const pct = levelTableValue(effect.primaryDamagePctByLevel, level);
      const primary = Math.floor((baseAttack * (100 + pct)) / 100);
      damage = dmg(primary);
      parts.push(`attack +${pct}% (${damage} dmg)`);
      break;
    }
    case 'sunder': {
      const pct = levelTableValue(effect.primaryDamagePctByLevel, level);
      // Consume vulnerable on target before/with hit — apply bonus from stacks.
      let vulnBonus = 0;
      if (targetId) {
        const target = nextEnemies.find((e) => e.id === targetId);
        const vuln = target?.statuses.find((s) => s.id === 'vulnerable');
        if (vuln && vuln.intensity > 0) {
          vulnBonus = vuln.intensity * 10;
          nextEnemies = nextEnemies.map((e) =>
            e.id === targetId
              ? {
                  ...e,
                  statuses: e.statuses.filter((s) => s.id !== 'vulnerable'),
                }
              : e,
          );
          parts.push(`vuln consumed +${vulnBonus}%`);
        }
      }
      const primary = Math.floor(
        (baseAttack * (100 + pct + vulnBonus)) / 100,
      );
      damage = dmg(primary);
      setStatus('vulnerable', effect.applyVulnerableStacks ?? 1, 99);
      parts.push(`attack +${pct}% (${damage} dmg)`, 'vulnerable +1');
      break;
    }
    case 'flurry': {
      const extras = levelTableValue(effect.extraAttacksByLevel, level);
      const pct = effect.extraAttackDamagePct ?? 60;
      const perHit = Math.floor(baseAttack * (pct / 100));
      const rng = options.rng;
      const combatStats = options.combatStats;
      let seq =
        options.attackSequence ?? { consecutiveCrits: 0 };
      const chainedHits: ChainedAttackHit[] = [];
      let hits = 0;
      for (let i = 0; i < extras; i += 1) {
        const living = nextEnemies.filter((e) => e.hp > 0);
        if (living.length === 0) break;
        const pick = rng
          ? living[rng.nextInt(0, living.length - 1)]!
          : living[0]!;

        // Existing Flurry damage: % of activating combo base only (no Attack % re-apply).
        // M7-D2: combo base excludes class flat Attack (option F1 — see
        // docs/M7D2_STAGE_B_CHAINED_ATTACK_OPTIONS.md). Do not silently add flat.
        let raw = perHit;
        let isCrit = false;
        if (rng && combatStats && raw > 0) {
          const extraPp = consecutiveCritBonusPp({
            consecutiveCritsSoFar: seq.consecutiveCrits,
            talentPp: combatStats.critChanceOnConsecutiveHitsPp,
            mode: options.consecutiveCritStackingMode,
          });
          const crit = rollCritDamage(raw, combatStats, rng.next(), {
            extraCritChancePp: extraPp,
          });
          raw = crit.damage;
          isCrit = crit.isCrit;
          seq = recordAttackCritOutcome(seq, isCrit);
        }

        let remaining = raw;
        let sh = pick.shield ?? 0;
        if (sh > 0) {
          const ab = Math.min(sh, remaining);
          sh -= ab;
          remaining -= ab;
        }
        const mit = Math.max(0, remaining - pick.intention.guardAmount);
        const hpAfter = Math.max(0, pick.hp - mit);
        nextEnemies = nextEnemies.map((e) => {
          if (e.id !== pick.id) return e;
          let updated: EnemyState = { ...e, hp: hpAfter, shield: sh };
          if (
            mit > 0 &&
            combatStats &&
            rng &&
            combatStats.bleedChancePp > 0
          ) {
            const chance = Math.min(100, combatStats.bleedChancePp) / 100;
            if (rng.next() < chance) {
              const existing = updated.statuses.find((s) => s.id === 'bleed');
              updated = {
                ...updated,
                statuses: existing
                  ? updated.statuses.map((s) =>
                      s.id === 'bleed'
                        ? { ...s, intensity: s.intensity + 1 }
                        : s,
                    )
                  : [
                      ...updated.statuses,
                      {
                        id: 'bleed' as const,
                        intensity: 1,
                        remainingTurns: 99,
                      },
                    ],
              };
            }
          }
          return updated;
        });
        totalDamageDealt += mit;
        damage += mit;
        hits += 1;
        chainedHits.push({
          source: 'flurry',
          targetId: pick.id,
          damage: mit,
          isCrit,
          targetHpAfter: hpAfter,
        });
        if (isCrit) {
          parts.push(`extra CRIT ${mit}`);
        }
      }
      parts.push(`${hits} extras @${pct}% (${damage} dmg)`);
      return {
        summary: `${skill.name} Lv${level}: ${parts.join(', ')}`,
        damage,
        shieldGained,
        statusApplied,
        player: nextPlayer,
        enemies: nextEnemies,
        targetId: targetId ?? undefined,
        totalDamageDealt,
        attackSequence: seq,
        chainedHits,
      };
    }
    case 'twin_strike': {
      const pct = levelTableValue(effect.offHandDamagePctByLevel, level);
      const combatStats = options.combatStats;
      const rng = options.rng;
      let seq =
        options.attackSequence ?? { consecutiveCrits: 0 };
      // Prefer snapshotted off-hand weapon swing; fall back to provisional share.
      // M7-D2 approved T2: after OH weapon × skill %, add full class flat Attack
      // once (never OH-penalised), then Attack % → independent crit.
      // See docs/M7D2_STAGE_B_CHAINED_ATTACK_OPTIONS.md.
      const offHandBase =
        combatStats && combatStats.weaponSwingOffHand > 0
          ? combatStats.weaponSwingOffHand
          : Math.floor(baseAttack * OFFHAND_DAMAGE_CONTRIBUTION);
      const twinDmgBonus = options.twinStrikeDamageBonusPct ?? 0;
      let raw = Math.floor(
        (offHandBase * (pct / 100) * (100 + twinDmgBonus)) / 100,
      );
      const classFlat = Math.max(0, Math.floor(combatStats?.flatAttack ?? 0));
      raw += classFlat;
      if (combatStats && targetId) {
        const target = nextEnemies.find((e) => e.id === targetId);
        if (target) {
          raw = applyOutgoingDamageBonuses(raw, combatStats, {
            targetHp: target.hp,
            targetMaxHp: target.maxHp,
            targetIsBleeding: target.statuses.some((s) => s.id === 'bleed'),
          });
        }
      }

      let isCrit = false;
      if (rng && combatStats && raw > 0) {
        const consecutivePp = consecutiveCritBonusPp({
          consecutiveCritsSoFar: seq.consecutiveCrits,
          talentPp: combatStats.critChanceOnConsecutiveHitsPp,
          mode: options.consecutiveCritStackingMode,
        });
        const extraPp =
          consecutivePp + (options.twinStrikeExtraCritChancePp ?? 0);
        const crit = rollCritDamage(raw, combatStats, rng.next(), {
          extraCritChancePp: extraPp,
        });
        raw = crit.damage;
        isCrit = crit.isCrit;
        seq = recordAttackCritOutcome(seq, isCrit);
      }

      // Bypass outer dmg() which would re-apply outgoing bonuses / skip crit.
      let dealt = 0;
      let hpAfter = 0;
      if (targetId && raw > 0) {
        const target = nextEnemies.find((e) => e.id === targetId);
        if (target && target.hp > 0) {
          let remaining = raw;
          let enemyShield = target.shield ?? 0;
          if (enemyShield > 0) {
            const absorbed = Math.min(enemyShield, remaining);
            enemyShield -= absorbed;
            remaining -= absorbed;
          }
          dealt = Math.max(0, remaining - target.intention.guardAmount);
          hpAfter = Math.max(0, target.hp - dealt);
          nextEnemies = nextEnemies.map((e) => {
            if (e.id !== targetId) return e;
            let updated: EnemyState = {
              ...e,
              hp: hpAfter,
              shield: enemyShield,
            };
            if (
              dealt > 0 &&
              combatStats &&
              rng &&
              combatStats.bleedChancePp > 0
            ) {
              const chance = Math.min(100, combatStats.bleedChancePp) / 100;
              if (rng.next() < chance) {
                const existing = updated.statuses.find((s) => s.id === 'bleed');
                updated = {
                  ...updated,
                  statuses: existing
                    ? updated.statuses.map((s) =>
                        s.id === 'bleed'
                          ? { ...s, intensity: s.intensity + 1 }
                          : s,
                      )
                    : [
                        ...updated.statuses,
                        {
                          id: 'bleed' as const,
                          intensity: 1,
                          remainingTurns: 99,
                        },
                      ],
                };
              }
            }
            return updated;
          });
          totalDamageDealt += dealt;
        }
      }
      damage = dealt;
      const chainedHits: ChainedAttackHit[] = targetId
        ? [
            {
              source: 'twin_strike',
              targetId,
              damage: dealt,
              isCrit,
              targetHpAfter: hpAfter,
            },
          ]
        : [];
      parts.push(
        combatStats && combatStats.weaponSwingOffHand > 0
          ? `off-hand ${pct}% (${damage} dmg${isCrit ? ' CRIT' : ''})`
          : `off-hand ${pct}% (${damage} dmg${isCrit ? ' CRIT' : ''}; provisional base)`,
      );
      return {
        summary: `${skill.name} Lv${level}: ${parts.join(', ')}`,
        damage,
        shieldGained,
        statusApplied,
        player: nextPlayer,
        enemies: nextEnemies,
        targetId: targetId ?? undefined,
        totalDamageDealt,
        attackSequence: seq,
        chainedHits,
      };
    }
    case 'iron_harvest': {
      const pct = levelTableValue(effect.shieldFromDamagePctByLevel, level);
      const dealt =
        (options.damageAlreadyDealt ?? 0) + totalDamageDealt;
      shieldGained = Math.floor(dealt * (pct / 100));
      nextPlayer = {
        ...nextPlayer,
        shield: nextPlayer.shield + shieldGained,
      };
      parts.push(`+${shieldGained} shield (${pct}% of ${dealt} dmg)`);
      break;
    }
    case 'kinetic_cascade': {
      const pct = levelTableValue(effect.shieldDamagePctByLevel, level);
      const raw = Math.floor(nextPlayer.shield * (pct / 100));
      damage = dmg(raw);
      if (effect.stunTurns && damage >= 0 && targetId) {
        setStatus('stun', 1, effect.stunTurns);
      }
      parts.push(`cascade ${pct}% shield (${damage} dmg)`, 'stun 1');
      break;
    }
    default: {
      parts.push('unhandled active');
    }
  }

  // Silence unused outer helpers (kept for signature parity)
  void applyDamage;
  void applyDamageTo;
  void applyStatus;
  void skill;

  return {
    summary: `${skill.name} Lv${level}: ${parts.join(', ')}`,
    damage,
    shieldGained,
    statusApplied,
    player: nextPlayer,
    enemies: nextEnemies,
    targetId: targetId ?? undefined,
    totalDamageDealt,
  };
}
